import { useEffect, useState } from 'react'
import { setLogLevel, LogLevel, RemoteTrackPublication, setLogExtension, Room } from 'livekit-client'
import { tinykeys } from 'tinykeys'

import { useRoomContext } from '../context'
import { clientLogError, clientLogWarn } from './clientLog'

import styles from '../../styles/Debug.module.css'

// LiveKit оборачивает methodFactory при каждом setLogExtension — повторный
// вызов на retry (новый Room) дублирует warn/error в Loki.
let livekitLokiExtensionInstalled = false

/**
 * Включает подробные логи LiveKit в консоли и шлёт warn/error в Loki.
 *
 * debug и info SDK остаются в DevTools через setLogLevel('debug'), но не едут
 * на сервер: иначе один join съедает минутный лимит и глушит ICE-телеметрию.
 *
 * Хук зовут из ActiveRoom до LiveKitRoom, чтобы логи не пропадали на retry.
 * Расширение логгера ставится один раз: SDK складывает обёртки, а не заменяет их.
 *
 * @param room - только для window.__lk_room; логи Loki от инстанса не зависят
 */
export const useDebugMode = ({
  logLevel,
  room,
}: {
  logLevel?: LogLevel
  room?: Room
} = {}) => {
  useEffect(() => {
    setLogLevel(logLevel ?? 'debug')

    if (livekitLokiExtensionInstalled) {
      return
    }

    livekitLokiExtensionInstalled = true

    setLogExtension((level, msg, context) => {
      if (level === LogLevel.warn) {
        clientLogWarn(msg, context)

        return
      }

      if (level === LogLevel.error) {
        clientLogError(msg, context)
      }
    })
  }, [logLevel])

  useEffect(() => {
    if (room) {
      // @ts-expect-error
      window.__lk_room = room
    }

    return () => {
      // @ts-expect-error
      window.__lk_room = undefined
    }
  }, [room])
}

/**
 * Overlay по Shift+D. Логирование сюда больше не привязано, чтобы оно не
 * пропадало вместе с LiveKitRoom на retry.
 */
export const DebugMode = (_props: { logLevel?: LogLevel }) => {
  const room = useRoomContext()

  const [isOpen, setIsOpen] = useState(false)
  const [roomSid, setRoomSid] = useState('')

  useEffect(() => {
    room.getSid().then(setRoomSid)
  }, [room])

  useEffect(() => {
    if (window) {
      const unsubscribe = tinykeys(window, {
        'Shift+D': () => {
          console.log('setting open')
          setIsOpen((open) => !open)
        },
      })

      return () => {
        unsubscribe()
      }
    }
  }, [isOpen])

  if (typeof window === 'undefined' || !isOpen) {
    return null
  }

  const lp = room.localParticipant

  return (
    isOpen && (
      <div className={styles.overlay}>
        <section id="room-info">
          <h3>
            Room Info {room.name}: {roomSid}
          </h3>
        </section>
        <details open>
          <summary>
            <b>Local Participant: {lp.identity}</b>
          </summary>
          <details open className={styles.detailsSection}>
            <summary>
              <b>Published tracks</b>
            </summary>
            <div>
              {Array.from(lp.trackPublications.values()).map((t) => (
                <>
                  <div>
                    <i>
                      {t.source.toString()}
                      &nbsp;<span>{t.trackSid}</span>
                    </i>
                  </div>
                  <table>
                    <tbody>
                      <tr>
                        <td>Kind</td>
                        <td>
                          {t.kind}&nbsp;
                          {t.kind === 'video' && (
                            <span>
                              {t.track?.dimensions?.width}x{t.track?.dimensions?.height}
                            </span>
                          )}
                        </td>
                      </tr>
                      <tr>
                        <td>Bitrate</td>
                        <td>{Math.ceil(t.track!.currentBitrate / 1000)} kbps</td>
                      </tr>
                    </tbody>
                  </table>
                </>
              ))}
            </div>
          </details>
          <details open className={styles.detailsSection}>
            <summary>
              <b>Permissions</b>
            </summary>
            <div>
              <table>
                <tbody>
                  {lp.permissions &&
                    Object.entries(lp.permissions).map(([key, val]) => (
                      <>
                        <tr>
                          <td>{key}</td>
                          {key !== 'canPublishSources' ? (
                            <td>{val.toString()}</td>
                          ) : (
                            <td> {val.join(', ')} </td>
                          )}
                        </tr>
                      </>
                    ))}
                </tbody>
              </table>
            </div>
          </details>
        </details>

        <details>
          <summary>
            <b>Remote Participants</b>
          </summary>
          {Array.from(room?.remoteParticipants.values()).map((p) => (
            <details key={p.sid} className={styles.detailsSection}>
              <summary>
                <b>
                  {p.identity}
                  <span></span>
                </b>
              </summary>
              <div>
                {Array.from(p.trackPublications.values()).map((t) => (
                  <>
                    <div>
                      <i>
                        {t.source.toString()}
                        &nbsp;<span>{t.trackSid}</span>
                      </i>
                    </div>
                    <table>
                      <tbody>
                        <tr>
                          <td>Kind</td>
                          <td>
                            {t.kind}&nbsp;
                            {t.kind === 'video' && (
                              <span>
                                {t.dimensions?.width}x{t.dimensions?.height}
                              </span>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td>Status</td>
                          <td>{trackStatus(t)}</td>
                        </tr>
                        {t.track && (
                          <tr>
                            <td>Bitrate</td>
                            <td>{Math.ceil(t.track.currentBitrate / 1000)} kbps</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </>
                ))}
              </div>
            </details>
          ))}
        </details>
      </div>
    )
  )
}

function trackStatus(t: RemoteTrackPublication): string {
  if (t.isSubscribed) {
    return t.isEnabled ? 'enabled' : 'disabled'
  } else {
    return 'unsubscribed'
  }
}
